import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { OAuthService } from 'angular-oauth2-oidc';
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	first,
	firstValueFrom,
	map,
	Observable,
	of,
	switchMap,
} from 'rxjs';
import { LoadingAnimationService } from 'src/app/modules/loading-animation/loading-animation.service';
import { ZEISSIdErrorResponseInterface } from 'src/app/types/interfaces/zeiss-id-error-response.api.interface';
import { environment } from 'src/environments/environment';
import { PopUpService, SimpleInfoPopupComponent, ToastService, ToastType } from 'vis-ods-angular';
import { RoutesEnum } from '../../routing/routes.enum';
import { ReportIssuePopupComponent } from '../../shared/components/pop-ups/report-issue-popup/report-issue-popup.component';
import { ServerErrorDisplayDataInterface } from '../../types/interfaces/server-error-display-data.interface';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
	constructor(
		private readonly oAuthService: OAuthService,
		private readonly toastService: ToastService,
		private readonly translocoService: TranslocoService,
		private readonly loadingAnimationService: LoadingAnimationService,
		private readonly popUpService: PopUpService,
		private readonly router: Router,
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown> | any> {
		return next.handle(request).pipe(
			catchError(async (err: HttpErrorResponse) => {
				if (err.status === 401) {
					// auto logout if 401 response returned from api

					this.oAuthService.logOut();
					this.oAuthService.initLoginFlow();
					throw new HttpErrorResponse(err.error);
				}
				if (err.status === 400 && err.url?.includes('OAuth/token')) {
					// If the token refresh fails redirect to login again
					this.oAuthService.redirectUri = window.location.href;
					this.oAuthService.logOut();
					this.oAuthService.initLoginFlow();
				}
				if (err.status === 500 && err.url?.includes('CountrySpecificSettings')) {
					this.router.navigate([RoutesEnum.noService]);
				}
				if (
					err.url?.includes(environment.apiBaseUrl) &&
					err.error?.Error?.Message !== 'InvalidVRXData'
				) {
					const errorMessageData = await this.getErrorMessage(err);
					this.addToast(errorMessageData);
				}
				if (err.error?.Error?.Message === 'InvalidVRXData') {
					this.displayinvalidVRXToast();
				}
				this.loadingAnimationService.close();
				const error = err.error?.message || err.statusText;
				// We need to throw the error again to be able to catch it in the component
				throw new HttpErrorResponse(error);
			}),
		);
	}
	private getErrorMessage(err: HttpErrorResponse): Promise<ServerErrorDisplayDataInterface> {
		return firstValueFrom(
			combineLatest([
				this.translocoService
					.selectTranslate(
						'errors.' + (err.error as ZEISSIdErrorResponseInterface)?.Error?.Message + '.text',
					)
					.pipe(
						switchMap((translation) =>
							translation.endsWith('.text')
								? this.translocoService.selectTranslate('errors.FallbackError.text')
								: of(translation),
						),
					),
				this.translocoService
					.selectTranslate(
						'errors.' + (err.error as ZEISSIdErrorResponseInterface)?.Error?.Message + '.headline',
					)
					.pipe(
						switchMap((translation) =>
							translation.endsWith('.headline')
								? this.translocoService.selectTranslate('errors.FallbackError.headline')
								: of(translation),
						),
					),
				this.translocoService.selectTranslate('errors.buttons.report'),
			]).pipe(
				map(([description, headline, reportButtonLabel]) => {
					return {
						description,
						headline,
						reportButtonLabel,
						errorId: (err.error as ZEISSIdErrorResponseInterface)?.Error?.ErrorId,
					};
				}),
			),
		);
	}
	private addToast(errorMessageData: ServerErrorDisplayDataInterface) {
		const errorToast = this.toastService.addToast({
			headline: errorMessageData.headline,
			variant: ToastType.error,
			description: errorMessageData.description,
			automaticDeleteTime: 10000,
			isButtonVisible: true,
			buttonLabel: errorMessageData.reportButtonLabel,
		});
		const subscription = errorToast.buttonClicked.subscribe(() => {
			this.popUpService.open(ReportIssuePopupComponent, {
				data: {
					headline: this.translocoService.translate(
						'generalComponents.reportIssuePopup.headlines.title',
					),
					description: this.translocoService.translate(
						'generalComponents.reportIssuePopup.texts.message',
					),
					errorId: errorMessageData.errorId,
				},
			});
		});
		errorToast.afterClosed.pipe(first()).subscribe(() => {
			subscription.unsubscribe();
		});
	}

	private displayinvalidVRXToast() {
		const vrxErrorToast = this.toastService.addToast({
			variant: ToastType.info,
			headline: this.translocoService.translate('errors.InvalidRXData.headline'),
			description: this.translocoService.translate('errors.InvalidRXData.text'),
			isButtonVisible: true,
			buttonLabel: this.translocoService.translate('errors.InvalidRXData.buttonLabel'),
			automaticDeleteTime: 5000,
		});
		const subscription = vrxErrorToast.buttonClicked.subscribe(() => {
			this.popUpService.open(SimpleInfoPopupComponent, {
				enableCloseButton: new BehaviorSubject<boolean>(true),
				data: {
					title: this.translocoService.translate('generalComponents.vrxErrorPopup.title'),
					texts: [
						{
							text: this.translocoService.translate('generalComponents.vrxErrorPopup.text'),
						},
					],
				},
			});
		});
		vrxErrorToast.afterClosed.pipe(first()).subscribe(() => {
			subscription.unsubscribe();
		});
	}
}
